import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  makeStyles,
  Typography,
} from '@material-ui/core';

import { FieldType } from '..';
import { FieldWithOptionsMultiSelectResponse } from '../../../.generated/admin-api';
import { InputValidationResult } from '../..';
import { apiTranslate } from '../../../translations/translations';
import { OptionResponse } from '../../../.generated/admin-api/models/OptionResponse';

type MultiOptionsFieldInputProps = {
  field: FieldType;
  required: boolean;
  value?: string[];
  disabled?: boolean;
  onChangeValidator: (value: Array<string>) => InputValidationResult;
};
interface Option {
  key: string;
  value: string;
}
const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    fontSize: '0.8rem',
  },
}));

const getAvailableOptions = (options?: Array<OptionResponse>): Option[] => {
  if (!options) {
    return [];
  }

  return options.map((option) => {
    return { key: option.key, value: apiTranslate(option.text) };
  });
};

const MultiOptionsField: React.FC<MultiOptionsFieldInputProps> = (props) => {
  const { field, disabled, value, onChangeValidator } = props;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const fieldWithMultiOptions = field as FieldWithOptionsMultiSelectResponse;

  const [selectedOptions, setSelectedOptions] = useState<Array<string>>(
    value || [],
  );

  // to deal with useState asynchronicity
  let newestOptions: string[];

  const toggleOption = (opt: string) => {
    if (!selectedOptions.includes(opt)) {
      const updated = [...selectedOptions, opt];
      setSelectedOptions(updated);
      newestOptions = updated;
    } else {
      const updated = selectedOptions.filter((o) => o !== opt);
      setSelectedOptions(updated);
      newestOptions = updated;
    }
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value as string;

    toggleOption(newValue);

    const validationResult = onChangeValidator(newestOptions);

    if (validationResult) {
      if (!validationResult.isValid) {
        const errorMessage =
          validationResult.errorMessage ??
          (validationResult.errorMessageKey
            ? t(
                validationResult.errorMessageKey,
                validationResult.errorMessageOptions,
              )
            : t('validation.default'));

        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(undefined);
      }
    }
  };

  return (
    <>
      <FormControl>
        {getAvailableOptions(fieldWithMultiOptions.options ?? undefined).map(
          (option) => (
            <FormControlLabel
              key={option.key}
              checked={selectedOptions.includes(option.key)}
              control={<Checkbox onChange={handleChange} value={option.key} />}
              label={option.value}
              disabled={disabled}
            />
          ),
        )}
      </FormControl>
      <Typography className={classes.error}>
        {!!errorMessage && errorMessage}
      </Typography>
    </>
  );
};

export { MultiOptionsField };
